import React, { useState } from 'react';
import { Smartphone, Users, Mail, Phone, User, MapPin, FileText, Calendar, CheckCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FormData {
  nome: string;
  email: string;
  telefone: string;
  bairro: string;
  idade: string;
  genero: string;
  profissao?: string;
  numeroLicenca?: string;
  tipoMota?: string;
  experiencia?: string;
  disponibilidade?: string;
}

interface RegisterSectionProps {
  setViewMode?: (mode: 'pessoal' | 'empresa' | 'privacy' | 'terms' | 'cookies' | 'help') => void;
}

const RegisterSection: React.FC<RegisterSectionProps> = ({ setViewMode }) => {
  const { t } = useTranslation();
  const isEng = t('common.loading') === 'Loading...';

  const [activeTab, setActiveTab] = useState<'user' | 'driver'>('user');
  const [formData, setFormData] = useState<FormData>({
    nome: '',
    email: '',
    telefone: '',
    bairro: '',
    idade: '',
    genero: '',
    profissao: '',
    numeroLicenca: '',
    tipoMota: '',
    experiencia: '',
    disponibilidade: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};

    // Validações comuns
    if (!formData.nome.trim()) newErrors.nome = isEng ? 'Name is required' : 'Nome é obrigatório';
    if (!formData.email.trim()) {
      newErrors.email = isEng ? 'Email is required' : 'Email é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = isEng ? 'Invalid email' : 'Email inválido';
    }
    if (!formData.telefone.trim()) {
      newErrors.telefone = isEng ? 'Phone is required' : 'Telefone é obrigatório';
    } else if (!/^\d{9}$/.test(formData.telefone.replace(/\s/g, ''))) {
      newErrors.telefone = isEng ? 'Phone must have 9 digits' : 'Telefone deve ter 9 dígitos';
    }
    if (!formData.bairro.trim()) newErrors.bairro = isEng ? 'Neighborhood is required' : 'Bairro é obrigatório';
    if (!formData.idade.trim()) {
      newErrors.idade = isEng ? 'Age is required' : 'Idade é obrigatória';
    } else if (parseInt(formData.idade) < 18) {
      newErrors.idade = isEng ? 'Must be over 18 years old' : 'Deve ser maior de 18 anos';
    }
    if (!formData.genero) newErrors.genero = isEng ? 'Gender is required' : 'Gênero é obrigatório';

    // Validações específicas para motoristas
    if (activeTab === 'driver') {
      if (!formData.numeroLicenca?.trim()) newErrors.numeroLicenca = isEng ? 'License number is required' : 'Número da licença é obrigatório';
      if (!formData.tipoMota) newErrors.tipoMota = isEng ? 'Motorcycle type is required' : 'Tipo de mota é obrigatório';
      if (!formData.experiencia) newErrors.experiencia = isEng ? 'Experience is required' : 'Experiência é obrigatória';
      if (!formData.disponibilidade) newErrors.disponibilidade = isEng ? 'Availability is required' : 'Disponibilidade é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    
    try {
      const { formHandler } = await import('../services/formHandler');
      const result = await formHandler.submitForm(formData, activeTab);
      
      if (result.success) {
        setSubmitSuccess(true);
        setFormData({
          nome: '', email: '', telefone: '', bairro: '', idade: '', genero: '',
          profissao: '', numeroLicenca: '', tipoMota: '', experiencia: '', disponibilidade: ''
        });
        setTimeout(() => setSubmitSuccess(false), 5000);
      } else {
        alert(result.message);
      }
    } catch (error) {
      alert(isEng ? 'Internal error. Try again later.' : 'Erro interno. Tente novamente mais tarde.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const bairrosLuanda = [
    'Talatona', 'Maianga', 'Ingombota', 'Rangel', 'Sambizanga', 'Kilamba Kiaxi',
    'Cazenga', 'Cacuaco', 'Viana', 'Belas', 'Alvalade', 'Maculusso'
  ];

  return (
    <section id="register" className="section-padding" style={{
      background: 'var(--light-gray)'
    }}>
      <div className="container">
        {/* Header */}
        <div style={{
          textAlign: 'center',
          marginBottom: '50px'
        }}>
          <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{
            color: 'var(--text-dark)',
          }}>
            {isEng ? "Join " : "Junte-se ao "}
            <span className="gradient-text">MotoRush</span>
          </h2>
          <p style={{
            fontSize: '18px',
            color: 'var(--text-light)',
            maxWidth: '600px',
            margin: '0 auto',
            lineHeight: '1.6'
          }}>
            {isEng 
                ? "Register now and become part of the transport revolution in Luanda" 
                : "Cadastre-se agora e faça parte da revolução do transporte em Luanda"}
          </p>
        </div>

        {/* Tab Selector */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '8px',
            display: 'flex',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}>
            <button
              onClick={() => setActiveTab('user')}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '15px 25px', borderRadius: '8px', border: 'none',
                background: activeTab === 'user' ? 'var(--gradient-primary)' : 'transparent',
                color: activeTab === 'user' ? 'white' : 'var(--text-dark)',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease'
              }}
            >
              <Smartphone size={20} />
              {isEng ? "I'm a Rider" : "Sou Passageiro"}
            </button>
            <button
              onClick={() => setActiveTab('driver')}
              style={{
                display: 'flex', alignItems: 'center', gap: '10px', padding: '15px 25px', borderRadius: '8px', border: 'none',
                background: activeTab === 'driver' ? 'var(--gradient-primary)' : 'transparent',
                color: activeTab === 'driver' ? 'white' : 'var(--text-dark)',
                fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease'
              }}
            >
              <Users size={20} />
              {isEng ? "I'm a Driver" : "Sou Motoqueiro"}
            </button>
          </div>
        </div>

        {/* Success Message */}
        {submitSuccess && (
          <div style={{
            background: 'linear-gradient(135deg, #10B981, #059669)', color: 'white', padding: '20px', borderRadius: '12px',
            marginBottom: '30px', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px'
          }}>
            <CheckCircle size={24} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              {isEng ? "Registration successful! We'll be in touch soon." : "Registo realizado com sucesso! Entraremos em contacto em breve."}
            </span>
          </div>
        )}

        {/* Form */}
        <div style={{
          maxWidth: '800px',
          margin: '0 auto'
        }}>
          <form onSubmit={handleSubmit} style={{
            background: 'white', borderRadius: '16px', padding: '40px', boxShadow: '0 8px 30px rgba(0, 0, 0, 0.1)'
          }}>
            {/* Form Header */}
            <div style={{
              textAlign: 'center',
              marginBottom: '30px'
            }}>
              <div style={{
                width: '60px', height: '60px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 15px', color: 'white',
                background: activeTab === 'user' ? 'var(--gradient-primary)' : 'var(--gradient-secondary)',
              }}>
                {activeTab === 'user' ? <Smartphone size={28} /> : <Users size={28} />}
              </div>
              <h3 style={{
                fontSize: '24px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '10px'
              }}>
                {activeTab === 'user' 
                    ? (isEng ? "Rider Registration" : "Cadastro de Passageiro") 
                    : (isEng ? "Driver Registration" : "Cadastro de Motoqueiro")}
              </h3>
              <p style={{
                fontSize: '14px', color: 'var(--text-light)'
              }}>
                {activeTab === 'user' 
                  ? (isEng ? 'Fill in your details to start using MotoRush' : 'Preencha seus dados para começar a usar o MotoRush')
                  : (isEng ? 'Join our network of partner drivers' : 'Junte-se à nossa rede de motoqueiros parceiros')
                }
              </p>
            </div>

            {/* Form Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Nome */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  <User size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  {isEng ? "Full Name *" : "Nome Completo *"}
                </label>
                <input
                  type="text"
                  value={formData.nome}
                  onChange={(e) => handleInputChange('nome', e.target.value)}
                  placeholder={isEng ? "Enter your full name" : "Digite seu nome completo"}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                    border: errors.nome ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.nome ? '#EF4444' : '#E5E7EB'}
                />
                {errors.nome && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.nome}</span>}
              </div>

              {/* Email */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  <Mail size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  {isEng ? "Email *" : "Email *"}
                </label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="name@email.com"
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                    border: errors.email ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.email ? '#EF4444' : '#E5E7EB'}
                />
                {errors.email && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.email}</span>}
              </div>

              {/* Telefone */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  <Phone size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  {isEng ? "Phone *" : "Telefone *"}
                </label>
                <input
                  type="tel"
                  value={formData.telefone}
                  onChange={(e) => handleInputChange('telefone', e.target.value)}
                  placeholder="9XX XXX XXX"
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                    border: errors.telefone ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.telefone ? '#EF4444' : '#E5E7EB'}
                />
                {errors.telefone && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.telefone}</span>}
              </div>

              {/* Bairro */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  <MapPin size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  {isEng ? "Neighborhood *" : "Bairro *"}
                </label>
                <select
                  value={formData.bairro}
                  onChange={(e) => handleInputChange('bairro', e.target.value)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease', background: 'white',
                    border: errors.bairro ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.bairro ? '#EF4444' : '#E5E7EB'}
                >
                  <option value="">{isEng ? "Select neighborhood" : "Selecione seu bairro"}</option>
                  {bairrosLuanda.map(bairro => (
                    <option key={bairro} value={bairro}>{bairro}</option>
                  ))}
                </select>
                {errors.bairro && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.bairro}</span>}
              </div>

              {/* Idade */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  <Calendar size={16} style={{ display: 'inline', marginRight: '5px' }} />
                  {isEng ? "Age *" : "Idade *"}
                </label>
                <input
                  type="number"
                  value={formData.idade}
                  onChange={(e) => handleInputChange('idade', e.target.value)}
                  placeholder="18"
                  min="18"
                  max="70"
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                    border: errors.idade ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.idade ? '#EF4444' : '#E5E7EB'}
                />
                {errors.idade && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.idade}</span>}
              </div>

              {/* Gênero */}
              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                  {isEng ? "Gender *" : "Gênero *"}
                </label>
                <select
                  value={formData.genero}
                  onChange={(e) => handleInputChange('genero', e.target.value)}
                  style={{
                    width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease', background: 'white',
                    border: errors.genero ? '2px solid #EF4444' : '2px solid #E5E7EB',
                  }}
                  onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                  onBlur={(e) => e.target.style.borderColor = errors.genero ? '#EF4444' : '#E5E7EB'}
                >
                  <option value="">{isEng ? "Select" : "Selecione"}</option>
                  <option value="masculino">{isEng ? "Male" : "Masculino"}</option>
                  <option value="feminino">{isEng ? "Female" : "Feminino"}</option>
                  <option value="outro">{isEng ? "Other" : "Outro"}</option>
                </select>
                {errors.genero && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.genero}</span>}
              </div>

              {/* Campos específicos para passageiros */}
              {activeTab === 'user' && (
                <div className="col-span-1 md:col-span-2">
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                    <FileText size={16} style={{ display: 'inline', marginRight: '5px' }} />
                    {isEng ? "Occupation" : "Profissão"}
                  </label>
                  <input
                    type="text"
                    value={formData.profissao}
                    onChange={(e) => handleInputChange('profissao', e.target.value)}
                    placeholder={isEng ? "E.g. Student, Public Employee, Entrepreneur..." : "Ex: Estudante, Funcionário público, Empresário..."}
                    style={{
                      width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                      border: '2px solid #E5E7EB',
                    }}
                    onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                    onBlur={(e) => e.target.style.borderColor = '#E5E7EB'}
                  />
                </div>
              )}

              {/* Campos específicos para motoristas */}
              {activeTab === 'driver' && (
                <>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                      <FileText size={16} style={{ display: 'inline', marginRight: '5px' }} />
                      {isEng ? "License Number *" : "Número da Licença *"}
                    </label>
                    <input
                      type="text"
                      value={formData.numeroLicenca}
                      onChange={(e) => handleInputChange('numeroLicenca', e.target.value)}
                      placeholder="Ex: 123456789"
                      style={{
                        width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease',
                        border: errors.numeroLicenca ? '2px solid #EF4444' : '2px solid #E5E7EB',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                      onBlur={(e) => e.target.style.borderColor = errors.numeroLicenca ? '#EF4444' : '#E5E7EB'}
                    />
                    {errors.numeroLicenca && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.numeroLicenca}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                      {isEng ? "Motorbike Type *" : "Tipo de Mota *"}
                    </label>
                    <select
                      value={formData.tipoMota}
                      onChange={(e) => handleInputChange('tipoMota', e.target.value)}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease', background: 'white',
                        border: errors.tipoMota ? '2px solid #EF4444' : '2px solid #E5E7EB',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                      onBlur={(e) => e.target.style.borderColor = errors.tipoMota ? '#EF4444' : '#E5E7EB'}
                    >
                      <option value="">{isEng ? "Select" : "Selecione"}</option>
                      <option value="convencional">{isEng ? "Conventional (Petrol)" : "Convencional (Gasolina)"}</option>
                      <option value="eletrica">{isEng ? "Electric" : "Elétrica"}</option>
                      <option value="ambas">{isEng ? "I have both" : "Tenho ambas"}</option>
                    </select>
                    {errors.tipoMota && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.tipoMota}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                      {isEng ? "Experience *" : "Experiência *"}
                    </label>
                    <select
                      value={formData.experiencia}
                      onChange={(e) => handleInputChange('experiencia', e.target.value)}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease', background: 'white',
                        border: errors.experiencia ? '2px solid #EF4444' : '2px solid #E5E7EB',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                      onBlur={(e) => e.target.style.borderColor = errors.experiencia ? '#EF4444' : '#E5E7EB'}
                    >
                      <option value="">{isEng ? "Select" : "Selecione"}</option>
                      <option value="menos-1-ano">{isEng ? "Less than 1 year" : "Menos de 1 ano"}</option>
                      <option value="1-3-anos">{isEng ? "1 to 3 years" : "1 a 3 anos"}</option>
                      <option value="3-5-anos">{isEng ? "3 to 5 years" : "3 a 5 anos"}</option>
                      <option value="mais-5-anos">{isEng ? "Over 5 years" : "Mais de 5 anos"}</option>
                    </select>
                    {errors.experiencia && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.experiencia}</span>}
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '600', color: 'var(--text-dark)', marginBottom: '8px' }}>
                      {isEng ? "Availability *" : "Disponibilidade *"}
                    </label>
                    <select
                      value={formData.disponibilidade}
                      onChange={(e) => handleInputChange('disponibilidade', e.target.value)}
                      style={{
                        width: '100%', padding: '12px', borderRadius: '8px', fontSize: '14px', transition: 'border-color 0.3s ease', background: 'white',
                        border: errors.disponibilidade ? '2px solid #EF4444' : '2px solid #E5E7EB',
                      }}
                      onFocus={(e) => e.target.style.borderColor = 'var(--primary-orange)'}
                      onBlur={(e) => e.target.style.borderColor = errors.disponibilidade ? '#EF4444' : '#E5E7EB'}
                    >
                      <option value="">{isEng ? "Select" : "Selecione"}</option>
                      <option value="tempo-integral">{isEng ? "Full-time (8+ hrs/day)" : "Tempo integral (8+ horas/dia)"}</option>
                      <option value="meio-periodo">{isEng ? "Part-time (4-6 hrs/day)" : "Meio período (4-6 horas/dia)"}</option>
                      <option value="fins-semana">{isEng ? "Weekends" : "Fins de semana"}</option>
                      <option value="flexivel">{isEng ? "Flexible hours" : "Horário flexível"}</option>
                    </select>
                    {errors.disponibilidade && <span style={{ fontSize: '12px', color: '#EF4444', marginTop: '5px', display: 'block' }}>{errors.disponibilidade}</span>}
                  </div>
                </>
              )}
            </div>

            {/* Submit Button */}
            <div style={{ marginTop: '30px', textAlign: 'center' }}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  background: isSubmitting ? '#9CA3AF' : 'var(--gradient-primary)',
                  color: 'white', border: 'none', padding: '15px 40px', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', minWidth: '200px'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(-2px)';
                    (e.target as HTMLButtonElement).style.boxShadow = '0 8px 25px rgba(255, 107, 53, 0.3)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSubmitting) {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                    (e.target as HTMLButtonElement).style.boxShadow = 'none';
                  }
                }}
              >
                {isSubmitting 
                    ? (isEng ? 'Sending...' : 'Enviando...') 
                    : (isEng ? 'Register Now' : 'Cadastrar Agora')}
              </button>
            </div>

            {/* Terms */}
            <div style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: 'var(--text-light)' }}>
              {isEng ? "By registering, you agree to our " : "Ao registar-se, concorda com os nossos "}
              <a 
                href="#terms" 
                onClick={(e) => {
                  e.preventDefault();
                  if (setViewMode) setViewMode('terms');
                }}
                style={{ color: 'var(--primary-orange)', textDecoration: 'none', cursor: 'pointer' }}
              >
                {isEng ? "Terms of Use" : "Termos de Uso"}
              </a>{' '}
              {isEng ? "and " : "e "}
              <a 
                href="#privacy" 
                onClick={(e) => {
                  e.preventDefault();
                  if (setViewMode) setViewMode('privacy');
                }}
                style={{ color: 'var(--primary-orange)', textDecoration: 'none', cursor: 'pointer' }}
              >
                {isEng ? "Privacy Policy" : "Política de Privacidade"}
              </a>
            </div>
          </form>
        </div>
      </div>
    </section>
  );
};

export default RegisterSection;
